/**
 * Utility functions for RegistrationPreview component
 */

import { CustomForm, FormType } from '../../types/CustomForm'

/**
 * Generic sport interface for background image resolution
 */
export interface GenericSport {
  id: string
  background_web_image?: string
  background_mobile_image?: string
}

/**
 * Sport interface for sport-specific logic
 */
export interface Sport {
  id: string
  name: string
}

/**
 * Determines the appropriate background image path based on sport type and device
 * @param genericSportForGame - Generic sport configuration object
 * @param isSoccer - Whether the sport is soccer
 * @param imageName - Default image name (legacy parameter, kept for compatibility)
 * @param isDesktop - Whether the device is desktop (determines web vs mobile image)
 * @param sport - Sport object containing sport details
 * @returns String path to the background image (without .png extension)
 *
 * @example
 * ```typescript
 * const bgImage = getBackgroundImage(
 *   genericSport,
 *   false,
 *   'game_hockey',
 *   true,
 *   { id: '1', name: 'hockey' }
 * )
 * // Returns: '/images/game_hockey' or genericSport.background_web_image
 * ```
 */
export const getBackgroundImage = (
  genericSportForGame: GenericSport | null,
  isSoccer: boolean,
  imageName: string,
  isDesktop: boolean,
  sport: Sport | null = null
): string => {
  console.log('imageName', imageName)

  if (!genericSportForGame) {
    if (isSoccer) {
      return '/images/soccer_game_background'
    } else {
      if (sport?.name) {
        if (sport.name === 'lacrosse') {
          return '/images/grass'
        }
        if (sport.name === 'fieldhockey') {
          return '/images/grass'
        }
        if (sport.name === 'floorball') {
          return '/images/floorball'
        }
      }
      return '/images/game_hockey'
    }
  } else {
    return isDesktop
      ? genericSportForGame.background_web_image?.replace('.png', '') ||
          '/images/game_hockey'
      : genericSportForGame.background_mobile_image?.replace('.png', '') ||
          '/images/game_hockey'
  }
}

/**
 * Finds a sport by ID from the sports array
 * @param sports - Array of sport objects
 * @param sportId - ID of the sport to find
 * @returns Sport object or undefined if not found
 *
 * @example
 * ```typescript
 * const sport = findSportById(sports, 'sport-123')
 * ```
 */
export const findSportById = (
  sports: Sport[],
  sportId: string
): Sport | undefined => {
  return sports?.find((s) => s?.id === sportId)
}

/**
 * Checks if a sport is soccer based on its name
 * @param sport - Sport object to check
 * @returns Boolean indicating if the sport is soccer
 *
 * @example
 * ```typescript
 * const isSoccer = isSoccerSport({ id: '1', name: 'soccer' })
 * // Returns: true
 * ```
 */
export const isSoccerSport = (sport: Sport | null): boolean => {
  return sport?.name === 'soccer'
}

/**
 * Determines if the registration uses a generic sport
 * @param registration - Registration object containing schedule information
 * @returns Boolean indicating if generic sport is used
 *
 * @example
 * ```typescript
 * const isGeneric = isGenericSport(registration)
 * ```
 */
export const isGenericSport = (registration: any): boolean => {
  return registration?.registration?.root_schedule?.generic_sport_id !== null
}

/**
 * Finds a generic sport by ID from the generic sport properties array
 * @param genericSportProperties - Array of generic sport property objects
 * @param genericSportId - ID of the generic sport to find
 * @returns Generic sport object or null if not found
 *
 * @example
 * ```typescript
 * const genericSport = findGenericSportById(genericSportProperties, 'generic-123')
 * ```
 */
export const findGenericSportById = (
  genericSportProperties: GenericSport[],
  genericSportId: string
): GenericSport | null => {
  return genericSportProperties?.find((g) => g?.id === genericSportId) || null
}

/**
 * Selects the appropriate form (team or player) from registration data
 * @param registration - Registration data containing team_form and player_form
 * @param formType - Type of form to select ('team' | 'player'), defaults to 'team'
 * @returns Selected CustomForm or null if not found
 *
 * @example
 * ```typescript
 * const selectedForm = getSelectedForm(registration, 'team')
 * // Returns: registration.team_form or null
 * ```
 */
export const getSelectedForm = (
  registration: any,
  formType: FormType = 'team'
): CustomForm | null => {
  if (!registration?.registration) {
    return null
  }

  const form =
    formType === 'team'
      ? registration.registration.team_form
      : registration.registration.player_form

  return form || null
}
